import React, { useMemo } from "react";
import { Card, InputNumber, DatePicker, Typography, Row, Col } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

const formatExpirationLabel = (exp) => {
  const date = dayjs(exp, "YYYYMMDD");
  return date.isValid() ? `${exp} (${date.format("ddd")})` : exp;
};

const DEFAULT_MAX_EXPECTED_MOVE = 25;

const ControlsCard = ({
  selectedExpiration,
  expectedMove,
  setExpectedMove,
  expectedDays,
  setExpectedDays,
  currentExpirationData,
  riskFreeRate,
  onRiskFreeRateChange,
  liveUnderlyingPrice,
  onLiveUnderlyingPriceChange,
  liveReferenceDate,
  onLiveReferenceDateChange,
}) => {
  const maxExpectedMove = useMemo(() => {
    const scenarios =
      currentExpirationData?.strikes?.[0]?.trading_days?.[0]?.scenarios;
    if (!scenarios) return DEFAULT_MAX_EXPECTED_MOVE;

    const moves = Object.keys(scenarios).map((key) => parseInt(key, 10));
    return moves.length ? Math.max(...moves) : DEFAULT_MAX_EXPECTED_MOVE;
  }, [currentExpirationData]);

  return (
    <Card style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Expiration Date:</Text>
          <div style={{ marginTop: 8, lineHeight: "32px" }}>
            <Text>{formatExpirationLabel(selectedExpiration)}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Expected Move (%):</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={1}
            max={maxExpectedMove}
            step={1}
            value={expectedMove}
            onChange={setExpectedMove}
            formatter={(value) => `${value}%`}
            parser={(value) => value.replace("%", "")}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Expected Days:</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={1}
            max={currentExpirationData?.trading_days_count || 10}
            value={expectedDays}
            onChange={setExpectedDays}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Risk-Free Rate (%):</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={0}
            max={20}
            step={0.1}
            precision={2}
            value={riskFreeRate}
            onChange={onRiskFreeRateChange}
            formatter={(value) => `${value}%`}
            parser={(value) => value.replace("%", "")}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Underlying Price ($):</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 8 }}
            min={0.01}
            step={0.01}
            precision={2}
            value={liveUnderlyingPrice}
            onChange={onLiveUnderlyingPriceChange}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Reference Time (local):</Text>
          <DatePicker
            style={{ width: "100%", marginTop: 8 }}
            value={liveReferenceDate}
            onChange={(date) => onLiveReferenceDateChange(date || dayjs())}
            format="YYYY-MM-DD HH:mm"
            showTime={{ format: "HH:mm" }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
            Entry price uses this instant (Eastern under the hood). Future
            prices use 16:00 ET market close.
          </Text>
        </Col>
      </Row>
    </Card>
  );
};

export default ControlsCard;
